import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, Award, Ban, Check, CheckSquare, Circle, CornerDownRight, History, Mail, MailCheck, MessageSquare, Pencil, PoundSterling, Square, StickyNote, Trash2, Users, UserPen } from 'lucide-react';
import { EditTraineeDetailsDialog } from '@/components/training/EditTraineeDetailsDialog';
import { TraineeMessageHistoryDialog } from '@/components/training/TraineeMessageHistoryDialog';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PAYMENT_METHOD_LABEL, PaymentHistoryPopover } from '@/components/training/paymentHistory';
import { TrainingXeroInvoice } from '@/components/training/TrainingXeroInvoice';

/** The trainee register table: filters, bulk actions, attendance, balances and per-row actions. */
export function CourseRegister({ addPaymentEntry, attendanceBusyIds, balanceFilter, courseId, data, deletePaymentEntry, deletingPayment, editPaymentEntry, fetchCourse, openPaymentModal, resendWelcome, resendingId, resolveBalance, selectedIds, setBalanceFilter, setBulkDeleteOpen, setDeleteReg, setEditForm, setEditReg, setMoveOpen, setMoveReg, setSelectedIds, toggleAttendance }) {
  const [detailsReg, setDetailsReg] = useState(null);
  const [historyReg, setHistoryReg] = useState(null);
  const toggleCancelled = async (r) => {
    const cancelling = !r.cancelled_at;
    if (cancelling && !window.confirm(`Mark ${r.name} as cancelled? They stay on the register but won't count as a no-show or in billed turnover.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations/${r.id}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cancelled: cancelling }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Update failed');
      toast.success(cancelling ? `${r.name} marked as cancelled` : `${r.name} reinstated`);
      fetchCourse();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      <EditTraineeDetailsDialog reg={detailsReg} courseId={courseId} onClose={() => setDetailsReg(null)} onSaved={fetchCourse} />
      <TraineeMessageHistoryDialog reg={historyReg} courseId={courseId} onClose={() => setHistoryReg(null)} />
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {data.registrations.length > 0 && (
            <button
              type="button"
              onClick={() => {
                // Toggle "select all" — if any are unchecked, check all;
                // otherwise clear.
                setSelectedIds((prev) => {
                  const allIds = data.registrations.map((r) => r.id);
                  if (allIds.every((id) => prev.has(id))) return new Set();
                  return new Set(allIds);
                });
              }}
              className="p-1 rounded hover:bg-gray-200 shrink-0"
              data-testid="register-select-all"
              aria-label="Select all trainees"
              title="Select all"
            >
              {data.registrations.length > 0 && data.registrations.every((r) => selectedIds.has(r.id))
                ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                : <Square className="w-4 h-4 text-gray-400" />}
            </button>
          )}
          <Users className="w-4 h-4 text-gray-500" />
          <h2 className="font-semibold text-gray-900">Register</h2>
          {selectedIds.size > 0 && (
            <span className="text-xs font-semibold text-sky-800 bg-sky-100 rounded-full px-2 py-0.5" data-testid="register-selected-count">
              {selectedIds.size} selected
            </span>
          )}
          {/* Reconcile CTA — surfaces when the course has imported rows
              whose structured payment fields are still empty (pre-fix
              imports read "balance_on_booking" as the current owed).
              One tap converts them to CURRENT balances so paid rows
              stop showing "DUE TODAY" on the register. */}
          {(() => {
            const stale = data.registrations.some((r) => {
              const bd = r.booking_details || {};
              const hasImportCols = !!(bd.balance_on_booking || bd.final_balance || bd.deposit_paid);
              const missingStructured = r.payment_total == null || r.balance_owed == null;
              return hasImportCols && missingStructured;
            });
            if (!stale) return null;
            return (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await fetch(
                      `${API_URL}/api/staff/training-courses/${courseId}/registrations/reconcile-imported-balances`,
                      { method: 'POST', credentials: 'include' },
                    );
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.detail || 'Reconcile failed');
                    }
                    const out = await res.json();
                    toast.success(`Fixed ${out.fixed} rows · ${out.marked_paid} marked paid`);
                    fetchCourse();
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid="reconcile-imported-btn"
                title="Recompute balances from spreadsheet 'Final Balance' column so paid trainees stop showing 'Due today'."
              >
                <PoundSterling className="w-4 h-4 mr-1.5" />
                Fix imported balances
              </Button>
            );
          })()}
        </div>
        {/* Bulk actions — only surface when at least one row is picked. */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2" data-testid="register-bulk-actions">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMoveOpen(true)}
              className="border-sky-300 text-sky-700 hover:bg-sky-50"
              data-testid="register-bulk-move-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-1.5" />
              Move
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkDeleteOpen(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="register-bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Delete
            </Button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs font-semibold text-gray-500 hover:text-gray-800"
              data-testid="register-clear-selection"
            >
              Clear
            </button>
          </div>
        )}
      </div>
      {/* Balance filter tabs — quick pivot between everyone / paid /
          still-owing. Guests inherit the visibility of their primary
          so a "Balance remaining" filter doesn't orphan a guest whose
          primary has cleared the fee. Counts help staff eyeball how
          many rows are in each bucket without switching first. */}
      {data.registrations.length > 0 && (() => {
        const paidPrimaryIds = new Set();
        const owedPrimaryIds = new Set();
        let paidCount = 0;
        let owedCount = 0;
        for (const r of data.registrations) {
          if (r.is_guest) continue;
          const b = resolveBalance(r);
          if (b.paid) { paidPrimaryIds.add(r.id); paidCount++; }
          else if (b.amount > 0) { owedPrimaryIds.add(r.id); owedCount++; }
        }
        const tabs = [
          { key: 'all',  label: 'View all', count: data.registrations.filter(r => !r.is_guest).length },
          { key: 'paid', label: 'Paid in full', count: paidCount },
          { key: 'owed', label: 'Balance remaining', count: owedCount },
        ];
        return (
          <div className="px-4 py-2 border-b bg-gray-50/70 flex flex-wrap items-center gap-1.5" data-testid="register-balance-filter">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setBalanceFilter(t.key)}
                className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${
                  balanceFilter === t.key
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`register-filter-${t.key}`}
                aria-pressed={balanceFilter === t.key}
              >
                {t.label}
                <span className={`ml-1.5 text-[10px] font-bold ${balanceFilter === t.key ? 'text-white/80' : 'text-gray-500'}`}>
                  ({t.count})
                </span>
              </button>
            ))}
          </div>
        );
      })()}
      {data.registrations.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">
          No-one on the register yet. Add trainees above.
        </div>
      ) : (
        <div className="divide-y">
          {(() => {
            // Filter primaries by balance state; guests always follow
            // their primary. Empty filter results get a helpful hint
            // instead of silently rendering nothing.
            const visiblePrimaryIds = new Set();
            for (const r of data.registrations) {
              if (r.is_guest) continue;
              if (balanceFilter === 'all') { visiblePrimaryIds.add(r.id); continue; }
              const b = resolveBalance(r);
              if (balanceFilter === 'paid' && b.paid) visiblePrimaryIds.add(r.id);
              else if (balanceFilter === 'owed' && !b.paid && b.amount > 0) visiblePrimaryIds.add(r.id);
            }
            const filtered = data.registrations.filter((r) => (
              r.is_guest
                ? visiblePrimaryIds.has(r.guest_of_registration_id)
                : visiblePrimaryIds.has(r.id)
            ));
            // Order: primaries as returned, each immediately followed by its +1 guests;
            // orphan guests (primary deleted) fall to the bottom.
            const byId = new Map(filtered.map((r) => [r.id, r]));
            const guestsOf = new Map();
            for (const r of filtered) {
              if (r.is_guest && r.guest_of_registration_id && byId.has(r.guest_of_registration_id)) {
                if (!guestsOf.has(r.guest_of_registration_id)) guestsOf.set(r.guest_of_registration_id, []);
                guestsOf.get(r.guest_of_registration_id).push(r);
              }
            }
            // Balances owed first (largest first), then paid — A–Z within each group.
            const primaries = filtered.filter((r) => !r.is_guest).sort((a, b) => {
              const ba = resolveBalance(a), bb = resolveBalance(b);
              const oa = !ba.paid && ba.amount > 0, ob = !bb.paid && bb.amount > 0;
              if (oa !== ob) return oa ? -1 : 1;
              if (oa && ob && ba.amount !== bb.amount) return bb.amount - ba.amount;
              return (a.name || '').localeCompare(b.name || '');
            });
            const ordered = [];
            for (const r of primaries) {
              ordered.push(r);
              for (const g of guestsOf.get(r.id) || []) ordered.push(g);
            }
            for (const r of filtered) if (r.is_guest && !(r.guest_of_registration_id && byId.has(r.guest_of_registration_id))) ordered.push(r);
            if (filtered.length === 0) {
              return (
                <div className="p-8 text-center text-gray-500 text-sm" data-testid="register-filter-empty">
                  {balanceFilter === 'paid' && 'No-one has paid in full yet.'}
                  {balanceFilter === 'owed' && 'Everyone is paid up — nothing outstanding.'}
                  {balanceFilter === 'all' && 'No rows to show.'}
                </div>
              );
            }
            return ordered.map(r => {
              const bal = resolveBalance(r);
              const attended = !!r.confirmed_at;
              const primary = r.is_guest ? byId.get(r.guest_of_registration_id) : null;
              return (
            <div
              key={r.id}
              className={`px-3 sm:px-4 py-1.5 transition-colors ${r.is_guest ? '!border-t-0 -mt-1' : ''} ${r.cancelled_at ? 'opacity-60 bg-rose-50/30' : ''} ${
                attended
                  ? 'bg-emerald-50/60'
                  : selectedIds.has(r.id) ? 'bg-sky-50/40' : r.is_guest ? 'bg-amber-50/40' : ''
              }`}
              data-testid={`registration-row-${r.id}`}
              data-guest-of={primary?.id || undefined}
            >
              {/* Header row: select checkbox + attendance toggle +
                  name/email + right cluster (balance pill + delete).
                  Uses flex without wrap so the right cluster always
                  stays on the same line as the name; the name column
                  has `min-w-0` so it can shrink and let its own
                  text wrap instead of pushing siblings to a new line. */}
              <div className={`flex items-center gap-2.5 ${r.is_guest ? 'pl-8 sm:pl-10' : ''}`}>
                {r.is_guest && (
                  <span className="shrink-0 -ml-5 sm:-ml-6 mr-0.5 text-amber-500 select-none" aria-hidden="true" title={primary ? `Plus-one booked with ${primary.name}` : 'Plus-one seat'}>
                    <CornerDownRight className="w-4 h-4" />
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                      return next;
                    });
                  }}
                  className="shrink-0"
                  data-testid={`register-select-${r.id}`}
                  aria-label={selectedIds.has(r.id) ? `Unselect ${r.name}` : `Select ${r.name}`}
                >
                  {selectedIds.has(r.id)
                    ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                    : <Square className="w-4 h-4 text-gray-400 hover:text-gray-600" />}
                </button>

                {/* Attendance toggle — compact circle/tick icon that
                    sits inline with the name so the row doesn't wrap
                    onto multiple lines on mobile. Empty circle = not
                    marked; solid green tick = attended. Full aria label
                    keeps it accessible for screen readers. */}
                <button
                  onClick={() => toggleAttendance(r)}
                  disabled={attendanceBusyIds.has(r.id)}
                  className={`shrink-0 w-6 h-6 rounded-full inline-flex items-center justify-center transition-colors ${
                    attended
                      ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                      : 'bg-white border-2 border-gray-300 text-gray-400 hover:border-emerald-400 hover:text-emerald-600'
                  } ${attendanceBusyIds.has(r.id) ? 'opacity-60 cursor-wait' : ''}`}
                  data-testid={`attendance-toggle-${r.id}`}
                  aria-label={attended ? `Un-mark ${r.name} as attended` : `Mark ${r.name} as attended`}
                  aria-pressed={attended}
                  title={attended ? 'Attended — tap to un-mark' : 'Tap to mark present'}
                >
                  {attended ? <Check className="w-3.5 h-3.5" /> : <Circle className="w-3 h-3" />}
                </button>
                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] leading-tight">
                  <Link
                    to={`/staff/trainees/${encodeURIComponent(r.email)}`}
                    className="text-sm font-semibold text-gray-900 hover:underline break-words"
                    data-testid={`trainee-name-link-${r.id}`}
                  >
                    {r.name}
                    {r.cancelled_at && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-800 bg-rose-100 rounded-full px-1.5 py-0.5 align-middle" data-testid={`trainee-cancelled-badge-${r.id}`} title={`Cancelled ${new Date(r.cancelled_at).toLocaleDateString('en-GB')}`}>
                        <Ban className="w-3 h-3" /> Cancelled
                      </span>
                    )}
                    {r.is_guest && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 rounded-full px-1.5 py-0.5 align-middle"
                        data-testid={`trainee-guest-badge-${r.id}`}
                        title={primary ? `Plus-one seat booked with ${primary.name}` : 'Plus-one seat booked with a primary trainee'}
                      >
                        +1{primary ? ` with ${primary.name}` : ''}
                      </span>
                    )}
                  </Link>
                  {r.guest_actual_name && (
                    <div className="text-xs text-amber-800 mt-0.5">
                      Attendee: <strong>{r.guest_actual_name}</strong>
                    </div>
                  )}
                  {(() => {
                    // Hide synthetic guest emails (guest+…@ox-guest.local).
                    // Show a "no contact" note instead so staff know to
                    // chase for details rather than seeing an ugly UUID.
                    const showEmail = !r.email_synthetic ? r.email : null;
                    const parts = [];
                    if (showEmail) parts.push(showEmail);
                    if (r.phone) parts.push(r.phone);
                    return (
                      <div className="text-xs text-gray-500 break-all truncate max-w-[400px] inline-flex items-center gap-1" title={parts.join(' · ')}>
                        <span className="truncate">{parts.length ? parts.join(' · ') : (
                          <span className="italic text-amber-700">No contact details yet</span>
                        )}</span>
                        <button type="button" onClick={(e) => { e.preventDefault(); setDetailsReg(r); }} className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-900 hover:bg-gray-100" title="Edit name, email, mobile & notes" aria-label={`Edit details for ${r.name}`} data-testid={`edit-trainee-${r.id}`}>
                          <UserPen className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={(e) => { e.preventDefault(); setHistoryReg(r); }} className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-900 hover:bg-gray-100" title="Every email & text sent to this trainee" aria-label={`Message history for ${r.name}`} data-testid={`trainee-messages-${r.id}`}>
                          <MessageSquare className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })()}
                  {r.confirmed_at && (
                    <div className="text-[11px] text-emerald-700 mt-0.5">
                      Confirmed {new Date(r.confirmed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  {r.move_history?.length > 0 && (() => {
                    const last = r.move_history[r.move_history.length - 1];
                    const fmtD = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'no date');
                    const fmtAt = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                    const full = r.move_history.map((m) => `${fmtAt(m.at)}: ${m.from_course_name || '?'} (${fmtD(m.from_course_date)}) → ${m.to_course_name || '?'} (${fmtD(m.to_course_date)})${m.by ? ` by ${m.by}` : ''}${m.notify_email || m.notify_sms ? ' · notified' : ''}`).join('\n');
                    return (
                      <div className="text-[11px] text-sky-700 mt-0.5 inline-flex items-center gap-1" title={full} data-testid={`trainee-moved-note-${r.id}`}>
                        <ArrowRightLeft className="w-3 h-3 shrink-0" />
                        Moved from {last.from_course_name || 'another course'} ({fmtD(last.from_course_date)}) on {fmtAt(last.at)}
                        {r.move_history.length > 1 && <span className="text-sky-500">· {r.move_history.length} moves</span>}
                      </div>
                    );
                  })()}
                  {/* Notes chip — subtle yellow post-it style so the
                      door person sees "cash on the day" at a glance
                      without needing to open the edit modal. */}
                  {r.booking_details?.notes && (
                    <div
                      className="inline-flex items-center gap-1 text-[11px] text-amber-900 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 max-w-[320px]"
                      data-testid={`trainee-note-chip-${r.id}`}
                      title={r.booking_details.notes}
                    >
                      <StickyNote className="w-3 h-3 shrink-0 opacity-70" />
                      <span className="truncate">{r.booking_details.notes}</span>
                    </div>
                  )}
                  {/* Free City & Guilds badge — recorded at booking so
                      revenue report knows a certificate was given away. */}
                  {r.free_city_guilds && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-800 bg-purple-100 border border-purple-200 rounded px-1.5 py-0.5"
                      data-testid={`trainee-cg-badge-${r.id}`}
                      title="Free City & Guilds certificate awarded at booking"
                    >
                      <Award className="w-3 h-3" />
                      Free C&amp;G
                    </div>
                  )}
                  {/* Multi-course booking + discount chip — surfaces
                      when this trainee booked several courses in one
                      go so revenue attribution is legible on the row. */}
                  {r.booking_group_id && (r.discount_amount || 0) > 0 && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5 ml-1"
                      data-testid={`trainee-discount-badge-${r.id}`}
                      title={`Multi-course discount £${Number(r.discount_amount).toFixed(2)} applied on this seat`}
                    >
                      −£{Number(r.discount_amount).toFixed(2)} discount
                    </div>
                  )}
                  {r.welcome_sent_at && (
                    <div className="text-[11px] text-gray-500 mt-0.5 inline-flex items-center gap-1" data-testid={`welcome-badge-${r.id}`}>
                      <MailCheck className="w-3 h-3 text-emerald-600" />
                      Welcome sent {new Date(r.welcome_sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </div>
                  )}
                  {(r.payment_total || r.payment_received) ? (
                    <div className="text-[11px] text-gray-500 whitespace-nowrap">
                      Fee £{Number(r.payment_total || 0).toFixed(2)} · Paid £{Number(r.payment_received || 0).toFixed(2)}
                      <span className="ml-1.5 inline-flex align-middle"><TrainingXeroInvoice reg={r} onChanged={fetchCourse} /></span>
                      {r.balance_paid_at && r.payment_method && (
                        <span className="ml-1 text-emerald-700 font-medium">
                          · Cleared via {PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}
                        </span>
                      )}
                      {!r.balance_paid_at && r.payment_method && (
                        <span className="ml-1 text-gray-600">
                          · Last: {PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}
                        </span>
                      )}
                    </div>
                  ) : null}
                  {(r.chases_sent && (r.chases_sent.soft || r.chases_sent.firm || r.chases_sent.final)) && (
                    (() => {
                      // Show the strongest chase step that's been sent so far.
                      const order = ['final', 'firm', 'soft'];
                      const label = { soft: 'Soft chase', firm: 'Firm chase', final: 'Final notice' };
                      const cls = { soft: 'bg-amber-50 text-amber-800', firm: 'bg-orange-100 text-orange-800', final: 'bg-rose-100 text-rose-800' };
                      const level = order.find(k => r.chases_sent[k]);
                      const at = new Date(r.chases_sent[level]);
                      const when = at.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                      return (
                        <span
                          className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded mt-1 inline-flex items-center gap-1 ${cls[level]}`}
                          title={`${label[level]} sent ${at.toLocaleString('en-GB')}`}
                          data-testid={`chase-status-${r.id}`}
                        >
                          {label[level]} · {when}
                        </span>
                      );
                    })()
                  )}
                </div>
                {/* Right-hand cluster (balance pill + delete) — grouped
                    so they always sit together on the same line as
                    the name. */}
                <div className="flex items-center gap-1.5 shrink-0">

                {!r.email_synthetic && <button
                  onClick={() => resendWelcome(r)}
                  disabled={resendingId === r.id}
                  className="h-7 px-2 rounded border font-medium border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50 text-xs"
                  data-testid={`resend-welcome-${r.id}`}
                  title={r.welcome_sent_at ? 'Re-send joining instructions + set-password link' : 'Send welcome email (joining instructions + set-password link)'}
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span className="hidden xl:inline">{resendingId === r.id ? 'Sending…' : (r.welcome_sent_at ? 'Re-send' : 'Send welcome')}</span>
                </button>}

                {(r.balance_owed || 0) > 0 ? (
                  <>
                    <button
                      onClick={() => openPaymentModal(r)}
                      className="h-7 text-xs px-2 rounded border font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1 whitespace-nowrap"
                      data-testid={`record-payment-${r.id}`}
                      title="Record a payment (amount + method)"
                    >
                      <PoundSterling className="w-3 h-3" />
                      Record payment
                    </button>
                    {(r.payment_history || []).length > 0 && (
                      <PaymentHistoryPopover
                        history={r.payment_history}
                        onDelete={(idx, entry) => deletePaymentEntry(r.id, idx, entry)}
                        deletingIndex={
                          deletingPayment
                          && !deletingPayment.startsWith('edit:')
                          && !deletingPayment.startsWith('add:')
                          && deletingPayment.startsWith(`${r.id}:`)
                            ? Number(deletingPayment.split(':')[1])
                            : null
                        }
                        onEdit={(idx, patch) => editPaymentEntry(r.id, idx, patch)}
                        editingIndex={
                          deletingPayment && deletingPayment.startsWith(`edit:${r.id}:`)
                            ? Number(deletingPayment.split(':')[2])
                            : null
                        }
                        onAdd={(payload) => addPaymentEntry(r.id, payload)}
                        addingPayment={deletingPayment === `add:${r.id}`}
                        defaultAmount={resolveBalance(r).amount}
                        testid={`payment-history-popover-${r.id}`}
                        trigger={
                          <button
                            type="button"
                            className="h-7 text-xs px-2 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1"
                            data-testid={`payment-history-trigger-${r.id}`}
                            title="See every payment recorded so far"
                            aria-label={`${r.payment_history.length} previous payments`}
                          >
                            <History className="w-3.5 h-3.5" />
                            {r.payment_history.length}
                          </button>
                        }
                      />
                    )}
                  </>
                ) : (r.payment_total || 0) > 0 ? (
                  <PaymentHistoryPopover
                    history={r.payment_history}
                    onDelete={(idx, entry) => deletePaymentEntry(r.id, idx, entry)}
                    deletingIndex={
                      deletingPayment
                      && !deletingPayment.startsWith('edit:')
                      && !deletingPayment.startsWith('add:')
                      && deletingPayment.startsWith(`${r.id}:`)
                        ? Number(deletingPayment.split(':')[1])
                        : null
                    }
                    onEdit={(idx, patch) => editPaymentEntry(r.id, idx, patch)}
                    editingIndex={
                      deletingPayment && deletingPayment.startsWith(`edit:${r.id}:`)
                        ? Number(deletingPayment.split(':')[2])
                        : null
                    }
                    onAdd={(payload) => addPaymentEntry(r.id, payload)}
                    addingPayment={deletingPayment === `add:${r.id}`}
                    defaultAmount={0}
                    testid={`payment-history-popover-${r.id}`}
                    trigger={
                      <button
                        type="button"
                        className="h-7 px-2 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1 text-xs cursor-pointer"
                        data-testid={`balance-cleared-${r.id}`}
                        title={`Paid in full${r.payment_method ? ` via ${PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}` : ''} — see every payment`}
                      >
                        <History className="w-3.5 h-3.5" />
                      </button>
                    }
                  />
                ) : (
                  <button
                    onClick={() => { setEditReg(r); setEditForm({ payment_total: '', payment_received: '' }); }}
                    className="h-7 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 inline-flex items-center gap-1 px-2 rounded border border-dashed border-gray-300 whitespace-nowrap"
                    data-testid={`balance-add-${r.id}`}
                  >
                    <Pencil className="w-3 h-3" /> Add fee
                  </button>
                )}
                  {bal.hasData && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditReg(r);
                        setEditForm({
                          payment_total: String(r.payment_total || ''),
                          payment_received: String(r.payment_received || ''),
                        });
                      }}
                      className={`h-7 px-2.5 rounded-full text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1 transition-colors ${
                        bal.paid
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : bal.amount >= 100
                            ? 'bg-rose-100 text-rose-800 hover:bg-rose-200'
                            : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                      }`}
                      data-testid={`balance-due-chip-${r.id}`}
                      title={bal.paid ? 'Balance settled — tap to edit fee' : `£${bal.amount.toFixed(2)} owing today — tap to record payment`}
                      aria-label={bal.paid ? `${r.name} paid in full` : `${r.name} owes £${bal.amount.toFixed(2)}`}
                    >
                      {bal.paid ? 'Paid' : `£${bal.amount.toFixed(2)} due`}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setMoveReg(r)}
                    className="p-1.5 rounded text-gray-400 hover:text-sky-700 hover:bg-sky-50"
                    aria-label={`Move ${r.name} to another course date`}
                    title="Move to another course date"
                    data-testid={`registration-move-${r.id}`}
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCancelled(r)}
                    className={`p-1.5 rounded ${r.cancelled_at ? 'text-rose-600 bg-rose-50 hover:bg-rose-100' : 'text-gray-400 hover:text-rose-600 hover:bg-rose-50'}`}
                    aria-label={r.cancelled_at ? `Reinstate ${r.name}` : `Mark ${r.name} as cancelled`}
                    title={r.cancelled_at ? 'Cancelled — tap to reinstate' : 'Mark as cancelled (kept on register, not counted as a no-show)'}
                    data-testid={`registration-cancel-${r.id}`}
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteReg(r)}
                    className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                    aria-label={`Remove ${r.name}`}
                    data-testid={`registration-delete-${r.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

            </div>
          );
          });
          })()}
        </div>
      )}
    </Card>
  );
}
